import { useState, useEffect } from 'react';
import { UserPlus, Mail, Lock, Phone, User, ArrowLeft, CheckCircle, ShieldCheck, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRouter } from '../components/Router';

export function RegisterPage() {
  const { signIn } = useAuth();
  const { navigate } = useRouter();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [redirectTo, setRedirectTo] = useState('/');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect');
    if (redirectUrl) {
      setRedirectTo(redirectUrl);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    try {
      // --- HTTPS FIX ---
      const siteUrl = 'https://argusautomobile.com';
      
      const confirmationUrl = `${siteUrl}/auth/callback?redirect=${encodeURIComponent(redirectTo)}`;
      
      console.log('Redirecting to:', confirmationUrl);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            phone_number: formData.phone,
          },
          emailRedirectTo: confirmationUrl,
        },
      });

      if (signUpError) {
        if (signUpError.message.includes('User already registered')) {
          setError('Un compte existe déjà avec cette adresse email');
        } else if (signUpError.message.includes('Password should be')) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
        } else if (signUpError.message.includes('Invalid email')) {
          setError('Adresse email invalide');
        } else {
          setError(signUpError.message || 'Erreur lors de la création du compte');
        }
        setLoading(false);
        return;
      }

      setSuccess(true);
      
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = "w-full pl-11 pr-4 py-3 bg-gray-100 border-0 rounded-2xl text-gray-900 placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-[#D5462E]/10 transition-all outline-none font-medium";

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-24 relative py-8 lg:py-0">
        <div className="absolute top-8 left-8 z-10">
          <a 
            href="/" 
            className="flex items-center text-sm font-semibold text-gray-500 hover:text-[#D5462E] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </a>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">Créer un compte</h1>
            <p className="text-gray-500 text-base">Rejoignez la plus grande communauté automobile.</p>
          </div>

          {success ? (
            <div className="bg-green-50/50 backdrop-blur-xl border border-green-100 rounded-3xl p-8 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-7 w-7 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Inscription réussie!</h2>
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                Un email de confirmation a été envoyé à <strong>{formData.email}</strong>.<br/>
                Veuillez vérifier votre boîte de réception et cliquer sur le lien de confirmation.
              </p>
              <div className="bg-amber-50/50 backdrop-blur-xl border border-amber-100 rounded-2xl p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-amber-800 text-left">
                    <p className="font-semibold mb-1">Prochaines étapes:</p>
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>Vérifiez votre email (y compris les spams)</li>
                      <li>Cliquez sur le lien de confirmation</li>
                      <li>Vous serez automatiquement redirigé</li>
                    </ol>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/connecter')}
                className="inline-flex items-center justify-center w-full bg-[#D5462E] text-white py-3 rounded-full font-semibold hover:bg-[#b83a24] transition-all"
              >
                Aller à la page de connexion
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-4 bg-red-50/50 backdrop-blur-xl border border-red-100 rounded-2xl flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nom complet</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                      <User className="h-5 w-5" />
                    </div>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={inputStyle}
                      placeholder="Ahmed Ben Ali"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                      <Mail className="h-5 w-5" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={inputStyle}
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                      <Phone className="h-5 w-5" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={inputStyle}
                      placeholder="+216 12 345 678"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mot de passe</label>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                        <Lock className="h-5 w-5" />
                      </div>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={inputStyle}
                        placeholder="••••••"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmer</label>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D5462E] transition-colors">
                        <Lock className="h-5 w-5" />
                      </div>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={inputStyle}
                        placeholder="••••••"
                        required
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D5462E] text-white py-3 rounded-full font-semibold hover:bg-[#b83a24] active:transform active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Création du compte...
                    </div>
                  ) : (
                    'Créer mon compte'
                  )}
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                <p className="text-gray-600 text-sm">
                  Vous avez déjà un compte ?{' '}
                  <a href="/connecter" className="text-[#D5462E] font-semibold hover:underline ml-1">
                    Se connecter
                  </a>
                </p>
                <div className="mt-5 flex items-center justify-center space-x-2 text-xs text-gray-400">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>Inscription sécurisée</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-bl from-[#D5462E]/30 to-black/70 z-10" />
        <img 
          src="https://i.ibb.co/JRxmqY0h/photo.png" 
          alt="Sports Car" 
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
      </div>
    </div>
  );
}